
import React, { useState, useMemo } from "react";
import { toast, ToastContainer } from "react-toastify";
import Heading from "../../components/Heading";
import ConfirmationModal from "../../components/ConfirmationModal";
import TanStackTable from "../../components/TanStackTable";
import { 
    useGetAllConfigs, 
    useDeleteConfig 
} from "../../queries/appConfigs";
import ConfigModal from "./ConfigModal";
import 'react-toastify/dist/ReactToastify.css';
import { FaPencilAlt, FaTrash } from "react-icons/fa";
import { getAdminType } from "../../utils/adminType";

const AppConfigs = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingConfig, setEditingConfig] = useState(null);
    const [deleteConfirmModal, setDeleteConfirmModal] = useState({ isOpen: false, configId: null });
    const [filters, setFilters] = useState({
        search: "",
        type: "",
        key: "",
        page: ""
    });

    // Queries and mutations
    const { data: configsData, isLoading, error, refetch } = useGetAllConfigs(filters);
    const { mutateAsync: deleteConfig, isPending: isDeleting } = useDeleteConfig();

    const configs = configsData?.data || [];

    const adminType = getAdminType();

    // Filter configs based on search and type
    const filteredConfigs = useMemo(() => {
        return configs.filter(config => {
            const matchesSearch = !filters.search || 
                config.key.toLowerCase().includes(filters.search.toLowerCase()) ||
                config.value.toLowerCase().includes(filters.search.toLowerCase());
            const matchesType = !filters.type || config.type === filters.type;
            const matchesKey = !filters.key || config.key.toLowerCase().includes(filters.key.toLowerCase());
            const matchesPage = !filters.page || 
                (config.page && config.page.toLowerCase().includes(filters.page.toLowerCase()));
            
            return matchesSearch && matchesType && matchesKey && matchesPage;
        });
    }, [configs, filters]);

    // Get unique types for filter dropdown
    const uniqueTypes = useMemo(() => {
        const types = configs.map(config => config.type);
        return [...new Set(types)];
    }, [configs]);

    // Define table columns
    const columns = useMemo(() => [
        {
            accessorKey: 'key',
            header: 'Key',
            cell: ({ getValue }) => (
                <span className="font-medium text-gray-900">{getValue()}</span>
            ),
            enableSorting: true,
            meta: {
                filterVariant: 'text'
            }
        },
        {
            accessorKey: 'value',
            header: 'Value',
            cell: ({ getValue, row }) => {
                const value = getValue();
                const type = row.original.type;
                
                if (type === 'html') {
                    return (
                        <div 
                            className="max-w-xs truncate cursor-pointer" 
                            title="Click to view full HTML content"
                            onClick={() => {
                                // Create a modal or tooltip to show full HTML content
                                const newWindow = window.open('', '_blank');
                                newWindow.document.write(`
                                    <html>
                                        <head><title>HTML Content - ${row.original.key}</title></head>
                                        <body style="font-family: Arial, sans-serif; padding: 20px;">
                                            <h2>Configuration: ${row.original.key}</h2>
                                            <hr>
                                            ${value}
                                        </body>
                                    </html>
                                `);
                                newWindow.document.close();
                            }}
                        >
                            <span className="text-blue-600 hover:text-blue-800">
                                {value.replace(/<[^>]*>/g, '').substring(0, 50)}...
                            </span>
                        </div>
                    );
                }
                
                return (
                    <div className="max-w-xs truncate" title={value}>
                        {value}
                    </div>
                );
            },
            enableSorting: false,
            meta: {
                filterVariant: 'text'
            }
        },
        {
            accessorKey: 'type',
            header: 'Type',
            align: 'center',
            cell: ({ getValue }) => (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {getValue()}
                </span>
            ),
            enableSorting: true,
            meta: {
                filterVariant: 'select',
                filterOptions: uniqueTypes.map(type => ({ value: type, label: type.charAt(0).toUpperCase() + type.slice(1) }))
            }
        },
        {
            accessorKey: 'page',
            header: 'Page',
            align: 'center',
            cell: ({ getValue }) => {
                const page = getValue();
                return page ? (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        {page}
                    </span>
                ) : (
                    <span className="text-gray-400">-</span>
                );
            },
            enableSorting: true,
            meta: {
                filterVariant: 'text'
            }
        },
        {
            accessorKey: 'updatedAt',
            header: 'Last Updated',
            align: 'center',
            cell: ({ getValue }) => (
                <span className="text-gray-500">
                    {new Date(getValue()).toLocaleDateString()}
                </span>
            ),
            enableSorting: true,
            meta: {
                filterVariant: 'date'
            }
        },
        {
            id: 'actions',
            header: 'Actions',
            // align: 'center',
            cell: ({ row }) => (
                <div className="flex gap-2">
                    <button
                        onClick={() => handleEdit(row.original)}
                        className="rounded p-1.5 bg-[rgba(52,199,89,0.1)] hover:bg-[rgba(52,199,89,0.2)] mx-1 min-h-7 min-w-7"
                    >
                        <FaPencilAlt className="fill-chipBlue" />
                    </button>
                    {adminType === "superadmin" && (
                        <button
                            onClick={() => handleDelete(row.original._id)}
                            className="rounded p-1.5 bg-[rgba(199,52,52,0.1)] hover:bg-[rgba(199,52,52,0.2)] mx-1 min-h-7"
                            disabled={isDeleting}
                        >
                            <FaTrash className="fill-chipRed" />
                        </button>
                    )}
                </div>
            ),
            enableSorting: false,
            enableColumnFilter: false
        }
    ], [uniqueTypes, isDeleting]);

    const handleCreate = () => {
        setEditingConfig(null);
        setIsModalOpen(true);
    };

    const handleEdit = (config) => {
        setEditingConfig(config);
        setIsModalOpen(true);
    };

    const handleDelete = async (configId) => {
        setDeleteConfirmModal({ isOpen: true, configId });
    };

    const confirmDelete = async () => {
        try {
            await deleteConfig(deleteConfirmModal.configId);
            toast.success("Configuration deleted successfully!");
            setDeleteConfirmModal({ isOpen: false, configId: null });
        } catch (error) {
            console.error("Error deleting config:", error);
            toast.error("Failed to delete configuration. Please try again.");
        }
    };

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const clearFilters = () => {
        setFilters({
            search: "",
            type: "",
            key: "",
            page: ""
        });
    };

    const handleModalClose = () => {
        setIsModalOpen(false);
        setEditingConfig(null);
    };

    if (error) {
        return (
            <div className="flex justify-center items-center min-h-[400px]">
                <div className="text-red-500 text-lg">
                    Error loading configurations: {error.message || "Unknown error"}
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col w-full">
            {/* Header */}
            <div className="flex justify-between items-center mb-6">
                <Heading text="System Configurations" />
                {adminType === "superadmin" && (
                    <button
                        onClick={handleCreate}
                        className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Add New Configuration
                    </button>
                )}
            </div>

            {/* Filters */}
            <div className="bg-white rounded-md shadow-[0px_4px_25px_rgba(94,92,154,0.1)] p-6 mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Filters</h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    {/* Search */}
                    <div className="flex flex-col">
                        <label className="text-base font-medium text-gray-700 mb-1">Search</label>
                        <input
                            type="text"
                            placeholder="Search by key or value..."
                            value={filters.search}
                            onChange={(e) => handleFilterChange('search', e.target.value)}
                            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        />
                    </div>

                    {/* Type Filter */}
                    <div className="flex flex-col">
                        <label className="text-base font-medium text-gray-700 mb-1">Type</label>
                        <select
                            value={filters.type}
                            onChange={(e) => handleFilterChange('type', e.target.value)}
                            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        >
                            <option value="">All Types</option>
                            {uniqueTypes.map(type => (
                                <option key={type} value={type}>{type}</option>
                            ))}
                        </select>
                    </div>

                    {/* Key Filter */}
                    <div className="flex flex-col">
                        <label className="text-base font-medium text-gray-700 mb-1">Key</label>
                        <input
                            type="text"
                            placeholder="Filter by key..."
                            value={filters.key}
                            onChange={(e) => handleFilterChange('key', e.target.value)}
                            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        />
                    </div>

                    {/* Page Filter */}
                    <div className="flex flex-col">
                        <label className="text-base font-medium text-gray-700 mb-1">Page</label>
                        <input
                            type="text"
                            placeholder="Filter by page..."
                            value={filters.page}
                            onChange={(e) => handleFilterChange('page', e.target.value)}
                            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        />
                    </div>
                </div>
                
                {/* Clear Filters */}
                <div className="mt-4">
                    <button
                        onClick={clearFilters}
                        className="px-4 py-2 text-base text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                    >
                        Clear Filters
                    </button>
                </div>
            </div>

            {/* Configurations List */}
            <div className="bg-white rounded-md pl-5 shadow-[0px_4px_25px_rgba(94,92,154,0.1)]">
                <div className="p-5 pl-0 border-b border-border">
                    <h3 className="text-lg font-semibold text-gray-900">
                        Configurations ({filteredConfigs.length})
                    </h3>
                </div>

                {isLoading ? (
                    <div className="flex justify-center items-center py-12">
                        <div className="text-lg text-gray-600">Loading configurations...</div>
                    </div>
                ) : filteredConfigs.length === 0 ? (
                    <div className="flex justify-center items-center py-12">
                        <div className="text-lg text-gray-500">
                            {configs.length === 0 ? "No configurations found." : "No configurations match your filters."}
                        </div>
                    </div>
                ) : (
                    <TanStackTable
                        data={filteredConfigs}
                        columns={columns}
                        enableFiltering={true}
                        enableSorting={true}
                        enablePagination={true}
                        defaultPageSize={10}
                        pageSizeOptions={[5, 10, 20, 50]}
                    />
                )}
            </div>

            {/* Config Modal */}
            {isModalOpen && (
                <ConfigModal
                    isOpen={isModalOpen}
                    onClose={handleModalClose}
                    config={editingConfig}
                    adminType={adminType}
                />
            )}

            {/* Delete Confirmation Modal */}
            <ConfirmationModal
                isOpen={deleteConfirmModal.isOpen}
                onClose={() => setDeleteConfirmModal({ isOpen: false, configId: null })}
                onConfirm={confirmDelete}
                title="Delete Configuration"
                message="Are you sure you want to delete this configuration? This action cannot be undone."
                confirmText="Delete"
                cancelText="Cancel"
                isLoading={isDeleting}
            />

            <ToastContainer position="top-right" />
        </div>
    );
};

export default AppConfigs;